import { useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';

import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface PanelProps {
  readonly icon: ReactNode;
  readonly title: string;
  readonly children: ReactNode;
  /** Optional right-aligned slot in the panel header (e.g. an edit pencil). */
  readonly headerAction?: ReactNode;
  /** When true, the header toggles a collapsed/expanded body. */
  readonly collapsible?: boolean;
  /** Initial open state when `collapsible`. Ignored otherwise. */
  readonly defaultOpen?: boolean;
  /** Optional drag handle slot rendered before the icon (for reorderable rails). */
  readonly dragHandle?: ReactNode;
  /**
   * When true, render only the panel BODY (no Card/header/collapse chrome) so the panel can be
   * embedded inside another card that owns the title + collapse (e.g. the pedigree node rail's
   * CollapsibleCard). Any `headerAction` is kept as a small right-aligned bar above the body so the
   * panel's own affordance (edit / add) survives; `collapsible`/`dragHandle` are ignored.
   */
  readonly embedded?: boolean;
}

/**
 * Right-rail panel primitive shared by every card in `PatientSidePanels`.
 * Header layout left-to-right: drag-handle? → icon → title → headerAction? →
 * chevron. The chevron always sits rightmost so the cards line up; the
 * title row and the chevron are independent buttons that share a toggle
 * handler so click anywhere on the labeled area still works.
 */
export function Panel({
  icon,
  title,
  children,
  headerAction,
  collapsible = false,
  defaultOpen = true,
  dragHandle,
  embedded = false,
}: PanelProps): ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const bodyVisible = !collapsible || open;
  const toggle = (): void => {
    setOpen((prev) => !prev);
  };

  // Embedded: the host card (CollapsibleCard) owns the title + collapse, so we drop our own chrome
  // and just render the body, keeping the header action available as a compact right-aligned bar.
  if (embedded) {
    return (
      <div>
        {headerAction !== undefined && (
          <div className="flex justify-end px-2 pt-2">{headerAction}</div>
        )}
        {children}
      </div>
    );
  }

  const headerInner = (
    <>
      <span className="text-muted-foreground shrink-0" aria-hidden="true">
        {icon}
      </span>
      <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground flex-1 min-w-0 text-start">
        {title}
      </h2>
    </>
  );

  return (
    <Card className="overflow-hidden">
      <div
        className={cn(
          'flex items-stretch',
          bodyVisible && 'border-b border-border',
        )}
      >
        {dragHandle !== undefined && (
          <span className="flex items-center ps-2 shrink-0">{dragHandle}</span>
        )}
        {collapsible ? (
          <button
            type="button"
            onClick={toggle}
            className="flex items-center gap-2 flex-1 min-w-0 px-4 py-2.5 hover:bg-muted/40 transition-colors"
            aria-expanded={open}
            aria-label={
              open
                ? t('patient.sidePanels.collapseAriaLabel', { title })
                : t('patient.sidePanels.expandAriaLabel', { title })
            }
          >
            {headerInner}
          </button>
        ) : (
          <div className="flex items-center gap-2 flex-1 min-w-0 px-4 py-2.5">
            {headerInner}
          </div>
        )}
        {headerAction !== undefined && (
          <span className="shrink-0 flex items-center pe-1">{headerAction}</span>
        )}
        {collapsible && (
          <button
            type="button"
            onClick={toggle}
            tabIndex={-1}
            aria-hidden="true"
            className="shrink-0 flex items-center px-3 py-2.5 text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
          >
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 transition-transform',
                !open && '-rotate-90',
              )}
              aria-hidden="true"
            />
          </button>
        )}
      </div>
      {bodyVisible && <div>{children}</div>}
    </Card>
  );
}
